import { useEffect, Component } from "react";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { FloatingCTAs } from "./components/FloatingCTAs";
import { EnquiryProvider } from "./components/EnquiryModal";
import Home from "./pages/Home";
import Kitchens from "./pages/Kitchens";
import KitchenDetail from "./pages/KitchenDetail";
import Carcass from "./pages/Carcass";
import Process from "./pages/Process";
import Materials from "./pages/Materials";
import Accessories from "./pages/Accessories";
import About from "./pages/About";
import Contact from "./pages/Contact";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { err: false };
  }
  static getDerivedStateFromError() {
    return { err: true };
  }
  render() {
    if (this.state.err) {
      return (
        <div className="grid min-h-screen place-items-center bg-ivory p-6 text-center">
          <div>
            <p className="font-display text-3xl text-ink">Something went wrong.</p>
            <button
              onClick={() => {
                this.setState({ err: false });
                window.location.href = "/";
              }}
              className="mt-6 rounded-full bg-royal px-7 py-3.5 text-sm font-semibold text-ivory"
            >
              Back to Home
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

const NotFound = () => (
  <div className="grid min-h-[70vh] place-items-center bg-ivory px-6 pt-24 text-center">
    <div>
      <p className="eyebrow text-golddim">404</p>
      <h1 className="mt-4 font-display text-4xl text-ink">This page is off the plan.</h1>
      <Link
        to="/"
        data-testid="not-found-home"
        className="mt-8 inline-flex rounded-full bg-royal px-7 py-3.5 text-sm font-semibold text-ivory"
      >
        Back to Home
      </Link>
    </div>
  </div>
);

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <EnquiryProvider>
          <ScrollToTop />
          <div className="grain min-h-screen">
            <Navbar />
            <main>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/kitchens" element={<Kitchens />} />
                <Route path="/kitchens/:slug" element={<KitchenDetail />} />
                <Route path="/carcass" element={<Carcass />} />
                <Route path="/process" element={<Process />} />
                <Route path="/materials" element={<Materials />} />
                <Route path="/accessories" element={<Accessories />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
            <Footer />
            <FloatingCTAs />
          </div>
          <Toaster position="top-center" richColors />
        </EnquiryProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
